import { lazy } from 'react';

// Device Management Suite (RMM)
// V3 surface unifies /devices + /device-groups + topology preview into one
// tabbed narrative. /devices/:id is its own V3 cockpit (DeviceDetail).
// DeviceGroups console (admin CRUD) stays at /device-groups for full
// create/edit flows; V3 Groups tab lists + drawer + links out to it.
export const DeviceList = lazy(() => import('@/capabilities/device-management/pages/device-management/Devices'));

export const DeviceOperationsShell = lazy(() => import('@/capabilities/device-management/pages/device-management/DeviceOperationsShell'));

export const DeviceDetail = lazy(() => import('@/capabilities/device-management/pages/device-management/DeviceDetail'));

export const RemoteControl = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/RemoteControl').then((m) => ({ default: m.RemoteControl }))
);

export const RemoteSession = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/RemoteSession').then((m) => ({ default: m.RemoteSession }))
);

export const PatchManagement = lazy(() => import('@/capabilities/device-management/pages/device-management/PatchManagement'));

// V3 surface unifies /agents (fleet list) and /agents/updates (rollout list)
// into one tabbed narrative. Deploy console (downloads/install-links),
// auto-provision wizard, and InstallLinks/PushWizard stay separate — they're
// admin/setup flows outside the V3 operator narrative. The V3 tab nav has
// informational Deploy/Auto-provision sections that link out to those.
export const AgentList = lazy(() => import('@/capabilities/device-management/pages/device-management/Agents'));

export const DeviceGroups = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/DeviceGroups').then((m) => ({ default: m.DeviceGroups }))
);

export const BroadcastCommand = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/BroadcastCommand').then((m) => ({ default: m.BroadcastCommand }))
);

export const DeviceHealthHistory = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/DeviceHealthHistory').then((m) => ({ default: m.DeviceHealthHistory }))
);

export const AlertCatalog = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/AlertCatalog').then((m) => ({ default: m.AlertCatalog }))
);

export const AlertDedupRules = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/AlertDedupRules').then((m) => ({ default: m.AlertDedupRules }))
);

export const DeviceGroupRules = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/DeviceGroupRules').then((m) => ({ default: m.DeviceGroupRules }))
);

export const PolicyManagement = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/PolicyManagement').then((m) => ({ default: m.PolicyManagement }))
);

export const MjolnirProfiles = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/MjolnirProfiles').then((m) => ({ default: m.MjolnirProfiles }))
);

export const MjolnirSessions = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/MjolnirSessions').then((m) => ({ default: m.MjolnirSessions }))
);

export const MjolnirPlaybooks = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/MjolnirPlaybooks').then((m) => ({ default: m.MjolnirPlaybooks }))
);

export const VendorProfiles = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/VendorProfiles').then((m) => ({ default: m.VendorProfiles }))
);

export const VendorDeploy = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/VendorDeploy').then((m) => ({ default: m.VendorDeploy }))
);

export const VendorStatus = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/VendorStatus').then((m) => ({ default: m.VendorStatus }))
);

export const HuntressDashboard = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/HuntressDashboard').then((m) => ({ default: m.HuntressDashboard }))
);

export const CloudflareGatewayDashboard = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/CloudflareGatewayDashboard').then((m) => ({ default: m.CloudflareGatewayDashboard }))
);

export const SecuritySuitePage = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/SecuritySuitePage').then((m) => ({ default: m.SecuritySuitePage }))
);

export const DNSFilterDashboard = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/DNSFilterDashboard').then((m) => ({ default: m.DNSFilterDashboard }))
);

export const DashlaneDashboard = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/DashlaneDashboard').then((m) => ({ default: m.DashlaneDashboard }))
);

export const IronscalesDashboard = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/IronscalesDashboard').then((m) => ({ default: m.IronscalesDashboard }))
);

export const CloudAllyDashboard = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/CloudAllyDashboard').then((m) => ({ default: m.CloudAllyDashboard }))
);

export const CodeTwoDashboard = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/CodeTwoDashboard').then((m) => ({ default: m.CodeTwoDashboard }))
);

export const PrintixDashboard = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/PrintixDashboard').then((m) => ({ default: m.PrintixDashboard }))
);

export const VendorDashboard = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/VendorDashboard').then((m) => ({ default: m.VendorDashboard }))
);

export const VendorDeploymentHistory = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/VendorDeploymentHistory').then((m) => ({
    default: m.VendorDeploymentHistory,
  }))
);

export const VendorOnboarding = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/VendorOnboarding').then((m) => ({ default: m.VendorOnboarding }))
);

export const AgentDeploy = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/AgentDeploy').then((m) => ({ default: m.AgentDeploy }))
);

export const InstallLinks = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/InstallLinks').then((m) => ({ default: m.InstallLinks }))
);

export const AgentPushWizard = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/AgentPushWizard').then((m) => ({ default: m.AgentPushWizard }))
);

// Keep the unified Agents overview at /agents?tab=updates, but mount the
// operational update console at its dedicated route. Pointing both routes at
// Agents made every "Open Updates console" action loop back to the read-only
// rollout cards and left package upload/push controls unreachable.
export const AgentUpdates = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/AgentUpdates').then((m) => ({
    default: m.AgentUpdates,
  }))
);

export const FleetOverview = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/FleetOverview').then((m) => ({ default: m.FleetOverview }))
);

export const AgentLogViewer = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/AgentLogViewer').then((m) => ({ default: m.AgentLogViewer }))
);

// BackstagePage removed — debug page deleted
export const MFTDashboard = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/MFTDashboard').then((m) => ({ default: m.MFTDashboard }))
);

export const MFTJobList = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/MFTJobList').then((m) => ({ default: m.MFTJobList }))
);

export const MFTJobDetail = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/MFTJobDetail').then((m) => ({ default: m.MFTJobDetail }))
);

export const MFTJobWizard = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/MFTJobWizard').then((m) => ({ default: m.MFTJobWizard }))
);

export const MFTEndpoints = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/MFTEndpoints').then((m) => ({ default: m.MFTEndpoints }))
);

export const AutoProvisionPage = lazy(() => import('@/capabilities/device-management/pages/device-management/AutoProvisionPage'));

export const EndpointRuntimeDashboard = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/EndpointRuntimeDashboard').then((m) => ({
    default: m.EndpointRuntimeDashboard,
  }))
);

export const FleetHealthDashboard = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/FleetHealthDashboard').then((m) => ({ default: m.FleetHealthDashboard }))
);

export const VulnerabilityDashboard = lazy(() => import('@/capabilities/device-management/pages/device-management/Vulnerabilities'));

export const SecurityIncidents = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/SecurityIncidents').then((m) => ({ default: m.SecurityIncidents }))
);

export const IncidentCockpit = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/IncidentCockpit').then((m) => ({ default: m.IncidentCockpit }))
);

export const QuarterlyReportPage = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/QuarterlyReportPage').then((m) => ({ default: m.QuarterlyReportPage }))
);

export const EventLogViewer = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/EventLogViewer').then((m) => ({ default: m.EventLogViewer }))
);

export const NetworkDocumentation = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/NetworkDocumentation').then((m) => ({ default: m.NetworkDocumentation }))
);

export const NetworkCredentials = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/NetworkCredentials').then((m) => ({ default: m.NetworkCredentials }))
);

export const NetworkDeviceConfigs = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/NetworkDeviceConfigs').then((m) => ({ default: m.NetworkDeviceConfigs }))
);

// App Store (Phase 29)
export const AppStore = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/AppStore').then((m) => ({ default: m.AppStore }))
);

export const AppDeployments = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/AppDeployments').then((m) => ({ default: m.AppDeployments }))
);

export const SoftwareRemovalPlanning = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/SoftwareRemovalPlanning').then((m) => ({ default: m.SoftwareRemovalPlanning }))
);

// Network Monitoring Intelligence (Sprint 450) — default exports
export const NetworkTopology = lazy(() => import('@/capabilities/device-management/pages/device-management/NetworkTopology'));

export const NetworkMonitoringAlerts = lazy(() => import('@/capabilities/device-management/pages/device-management/NetworkMonitoringAlerts'));

export const NetworkMonitoringIntelligence = lazy(
  () => import('@/capabilities/device-management/pages/device-management/NetworkMonitoringIntelligence')
);

export const UPSDashboard = lazy(() => import('@/capabilities/device-management/pages/device-management/UPSDashboard'));

// Device Assessment Reports
export const DeviceAssessmentReports = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/DeviceAssessmentReports').then((m) => ({
    default: m.DeviceAssessmentReports,
  }))
);

// SiteAssessment (Infrastructure Scanning)
export const SiteAssessmentScanResults = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/SiteAssessmentScanResults').then((m) => ({
    default: m.SiteAssessmentScanResults,
  }))
);

// Config Drift & GPO (Phase 30)
export const ConfigBaselines = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/ConfigBaselines').then((m) => ({ default: m.ConfigBaselines }))
);

export const ConfigDrift = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/ConfigDrift').then((m) => ({ default: m.ConfigDrift }))
);

export const GPOManager = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/GPOManager').then((m) => ({ default: m.GPOManager }))
);

// Agent Config Policies (Unified Agent - Phase 5)
export const AgentConfigPolicies = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/AgentConfigPolicies').then((m) => ({ default: m.AgentConfigPolicies }))
);

export const AgentConfigDetail = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/AgentConfigDetail').then((m) => ({ default: m.AgentConfigDetail }))
);

// Script Library (Phase 28)
export const ScriptDetail = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/ScriptDetail').then((m) => ({ default: m.ScriptDetail }))
);

// Playbook Library (KB auto-remediation)
export const PlaybookLibrary = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/PlaybookLibrary').then((m) => ({ default: m.PlaybookLibrary }))
);

export const PlaybookDetail = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/PlaybookDetail').then((m) => ({ default: m.PlaybookDetail }))
);

// Registry Editor (T-063)
export const RegistryEditor = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/RegistryEditor').then((m) => ({ default: m.RegistryEditor }))
);

// CW RMM Device Import
export const CwRmmImport = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/CwRmmImport').then((m) => ({ default: m.CwRmmImport }))
);

// Hygiene Engine
export const HygieneDashboard = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/HygieneDashboard').then((m) => ({ default: m.HygieneDashboard }))
);

export const HygienePolicyDetail = lazy(() =>
  import('@/capabilities/device-management/pages/device-management/HygienePolicyDetail').then((m) => ({ default: m.HygienePolicyDetail }))
);

// Disruption Planning
export const PlanningQueue = lazy(() =>
  import('@/capabilities/device-management/pages/disruption/PlanningQueue').then((m) => ({ default: m.PlanningQueue }))
);
